import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { ListToolbar, PageHeader, PageShell } from '~/components/layout';
import {
  Badge,
  Button,
  Card,
  CardBody,
  FilterDrawer,
  FormActions,
  DataTable,
  type DataTableColumn,
  type DropdownMenuItem,
  Modal,
  ModalActions,
  Pagination,
  PanelTable,
  type PanelTableColumn,
  RowActionsMenu,
  SkeletonRows,
} from '~/components/ui';
import { Select, Switch, TextField } from '~/components/form';
import { StatTile, StatTileSkeleton, StatsRow } from '~/components/dashboard';
import { useListUrlState } from '~/hooks/useListUrlState';
import { feedsOf, matchesCustomerFilters, matchesCustomerSearch } from '~/lib/customerFilters';
import { clampPage, pageCount } from '~/lib/listParams';
import type { CustomerAgent } from '~/modules/query/customers';
import {
  useGetCustomersQuery,
  useLazyGetCustomerAgentsQuery,
  useSaveAgentsMutation,
  useSaveParentAccountIdMutation,
} from '~/modules/query/customers';
import { queryErrorMessage } from '~/modules/query/base';
import { canOnboardCustomers } from '~/app/access';
import { routes } from '~/app/routes';
import { useUrlParams } from '~/hooks/useUrlParams';
import { getAccess } from '~/modules/access';

const CUSTOMER_OPS_SUBTITLE =
  'Operational view of every customer across Office and Social: feeds, servers, enabled products, and SMC agent sync.';

const STAT_TITLES = {
  total: 'Total customers',
  office: 'Office',
  shared: 'Shared',
  social: 'Social',
};

type Platform = 'Office' | 'Shared' | 'Social';

/** One company row from GET /customers (the fields this page reads). */
interface Customer {
  companyid: string | number;
  company_name?: string;
  platform?: Platform | string;
  mlsids?: string | string[];
  dataserver?: string;
  domain?: string | string[];
  website?: string;
  bay_parent_account_id?: string | number | null;
}

// URL keys of the drawer's filters.
const FILTER_KEYS = ['platform', 'feed', 'dataserver'];
const PLATFORMS: Platform[] = ['Office', 'Shared', 'Social'];

const platformColor = (p?: string) =>
  p === 'Social' ? 'info' : p === 'Shared' ? 'gray' : 'primary';

// Values arrive either as bare domain names (ClientBAY CustomerDomains) or full URLs
// (Customers.website, SocialBAY website_url). Display bare, link to https://www.<domain>.
const bareDomain = (value?: string | number | null) =>
  value
    ?.toString()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '');

const DASH = <span className="text-gray-400 dark:text-gray-500">—</span>;
const NAME_CLASS = 'font-medium text-gray-800 dark:text-white/90';

const DomainLink = ({ value }: { value?: string | null }) => {
  const bare = bareDomain(value);
  if (!bare) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  return (
    <a
      href={`https://www.${bare}`}
      target="_blank"
      rel="noreferrer"
      className="text-brand-500 hover:underline dark:text-brand-400"
    >
      {bare}
    </a>
  );
};

const domainsOf = (customer?: Customer | null): string[] =>
  Array.isArray(customer?.domain) ? customer.domain : customer?.domain ? [customer.domain] : [];

const customerLabel = (customer?: Customer | null) =>
  customer?.company_name ?? customer?.companyid ?? '';

// First item inline, the rest behind a "+N more" link that opens the full list.
const FirstAndMore = ({
  items,
  render = (item) => item,
  onMore,
}: {
  items: string[];
  render?: (item: string) => React.ReactNode;
  onMore: () => void;
}) => {
  if (!items.length) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  return (
    <span className="flex max-w-60 items-center gap-2 whitespace-nowrap">
      <span className="truncate">{render(items[0])}</span>
      {items.length > 1 && (
        <Button variant="link" className="shrink-0 text-xs" onClick={onMore}>
          +{items.length - 1} more
        </Button>
      )}
    </span>
  );
};

interface AgentSelection {
  listings: boolean;
  profiles: boolean;
  logins: boolean;
}
type AgentSelectionKind = keyof AgentSelection;

export const Customers = () => {
  const list = useListUrlState({ filterKeys: FILTER_KEYS });
  const url = useUrlParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const customersQuery = useGetCustomersQuery();
  const customers = customersQuery.data as Customer[] | undefined;
  const [loadAgents] = useLazyGetCustomerAgentsQuery();
  const [saveAgentsRequest, saveAgentsState] = useSaveAgentsMutation();
  const [saveParentAccountIdRequest, saveParentAccountIdState] = useSaveParentAccountIdMutation();
  const isSaving = saveAgentsState.isLoading || saveParentAccountIdState.isLoading;

  const [agentsModal, setAgentsModal] = useState<Customer | null>(null);
  const [agents, setAgents] = useState<CustomerAgent[]>();
  const [agentSelections, setAgentSelections] = useState<Record<string, AgentSelection>>({});
  const [initialSelections, setInitialSelections] = useState<Record<string, AgentSelection>>({});

  const [parentIdModal, setParentIdModal] = useState<Customer | null>(null);
  const [parentIdValue, setParentIdValue] = useState('');

  const [domainsModal, setDomainsModal] = useState<Customer | null>(null);
  const [feedsModal, setFeedsModal] = useState<Customer | null>(null);

  const filtered = Boolean(list.q) || list.filterCount > 0;

  const filteredCustomers = useMemo(
    () =>
      (customers ?? []).filter(
        (customer) =>
          matchesCustomerFilters(customer, {
            platform: list.filters.platform,
            feed: list.filters.feed,
            dataserver: list.filters.dataserver,
          }) &&
          (!list.q || matchesCustomerSearch(customer, list.q)),
      ),
    [customers, list.filters, list.q],
  );

  const pages = pageCount(filteredCustomers.length, list.pageSize);
  const page = clampPage(list.page, filteredCustomers.length, list.pageSize);
  const pageRows = filteredCustomers.slice((page - 1) * list.pageSize, page * list.pageSize);

  const filterOptions = useMemo(() => {
    const sorted = (values: (string | undefined)[]) =>
      [...new Set(values.filter((v): v is string => Boolean(v)))].sort((a, b) =>
        a.toString().localeCompare(b.toString()),
      );
    return {
      feed: sorted((customers ?? []).flatMap(feedsOf)),
      dataserver: sorted((customers ?? []).map((c) => c.dataserver)),
    };
  }, [customers]);

  const stats = useMemo(() => {
    const list = customers ?? [];
    return {
      total: list.length,
      office: list.filter((c) => c.platform === 'Office').length,
      shared: list.filter((c) => c.platform === 'Shared').length,
      social: list.filter((c) => c.platform === 'Social').length,
    };
  }, [customers]);

  const openAgentsModal = (customer: Customer) => {
    setAgentsModal(customer);
    setAgents(undefined);
    setAgentSelections({});
    setInitialSelections({});
    loadAgents(customer.companyid).then((result) => {
      if (result.error) {
        console.error(result.error);
        toast.error(
          queryErrorMessage(result.error) ?? 'The agents could not be loaded. Try again.',
        );
        setAgents([]);
        return;
      }

      const list = result.data ?? [];
      // Listings/Profile are one-shot actions (they start unchecked); Allow Login mirrors the
      // agent's current smc_login. Keeping both the initial and working copies lets Save send
      // only the deltas.
      const buildSelections = (): Record<string, AgentSelection> =>
        Object.fromEntries(
          list.map((agent) => [
            agent.publicid,
            { listings: false, profiles: false, logins: agent.smc_login === 'T' },
          ]),
        );
      setAgents(list);
      setAgentSelections(buildSelections());
      setInitialSelections(buildSelections());
    });
  };

  // Enable Social hands off here (`?sync=<id>`) to sync the customer's remaining agents; consumed once.
  const syncId = url.get('sync');
  useEffect(() => {
    if (!syncId || !customers) return;
    url.set({ sync: null });
    const customer = customers.find((c) => String(c.companyid) === syncId);
    if (customer) openAgentsModal(customer);
  }, [syncId, customers]);

  const toggleAgent = (publicid: string, kind: AgentSelectionKind) => {
    setAgentSelections((prev) => ({
      ...prev,
      [publicid]: { ...prev[publicid], [kind]: !prev[publicid]?.[kind] },
    }));
  };

  // Only what the user changed since the modal opened.
  const agentChanges = useMemo(() => {
    const listings: string[] = [],
      profiles: string[] = [],
      enableLogins: string[] = [],
      disableLogins: string[] = [];

    for (const [publicid, now] of Object.entries(agentSelections)) {
      const was: Partial<AgentSelection> = initialSelections[publicid] ?? {};

      if (now.listings) {
        listings.push(publicid);
      }
      if (now.profiles) {
        profiles.push(publicid);
      }
      if (now.logins && !was.logins) {
        enableLogins.push(publicid);
      }
      if (!now.logins && was.logins) {
        disableLogins.push(publicid);
      }
    }

    return { listings, profiles, enableLogins, disableLogins };
  }, [agentSelections, initialSelections]);

  const changeCount =
    agentChanges.listings.length +
    agentChanges.profiles.length +
    agentChanges.enableLogins.length +
    agentChanges.disableLogins.length;

  const saveAgents = async () => {
    if (!agentsModal) return;
    try {
      await saveAgentsRequest({ companyID: agentsModal.companyid, ...agentChanges }).unwrap();
      toast.success('Agent sync saved');
      setAgentsModal(null);
    } catch (err) {
      console.error(err);
      toast.error(queryErrorMessage(err) ?? 'The agents could not be saved. Try again.');
    }
  };

  const saveParentId = async () => {
    if (!parentIdModal) return;
    try {
      await saveParentAccountIdRequest({
        companyID: parentIdModal.companyid,
        BAYParentAccountID: parentIdValue,
      }).unwrap();
      toast.success('Parent account ID saved');
      setParentIdModal(null);
      setParentIdValue('');
    } catch (err) {
      console.error(err);
      toast.error(queryErrorMessage(err) ?? 'The parent account ID could not be saved. Try again.');
    }
  };

  const customerColumns: DataTableColumn<Customer>[] = [
    {
      // No customer record route exists yet (Customers has none in the plan), so rows are inert.
      id: 'name',
      header: 'Name',
      cell: (customer) =>
        customer.company_name ? (
          <>
            <span className={NAME_CLASS}>{customer.company_name}</span>
            <span className="block font-mono text-xs text-gray-500 dark:text-gray-400">
              {customer.companyid}
            </span>
          </>
        ) : (
          <span className={NAME_CLASS}>{customer.companyid}</span>
        ),
    },
    {
      id: 'platform',
      header: 'Platform',
      cell: (customer) =>
        customer.platform ? (
          <Badge size="sm" color={platformColor(customer.platform)} className="whitespace-nowrap">
            {customer.platform}
          </Badge>
        ) : (
          DASH
        ),
    },
    {
      id: 'feeds',
      header: 'Feeds',
      cell: (customer) => (
        <FirstAndMore items={feedsOf(customer)} onMore={() => setFeedsModal(customer)} />
      ),
    },
    { id: 'dataserver', header: 'Data server', cell: (customer) => customer.dataserver || DASH },
    {
      id: 'sites',
      header: 'Sites',
      cell: (customer) => (
        <FirstAndMore
          items={domainsOf(customer)}
          render={(d) => <DomainLink value={d} />}
          onMore={() => setDomainsModal(customer)}
        />
      ),
    },
    {
      id: 'office',
      header: 'Office',
      cell: (customer) => (
        <span className="block max-w-55 truncate">
          <DomainLink value={customer.website} />
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (customer) => {
        if (customer.platform === 'Social') return null;
        const items: DropdownMenuItem[] = [
          customer.bay_parent_account_id
            ? {
                label: 'Social sync',
                testId: 'social-sync',
                onClick: () => openAgentsModal(customer),
              }
            : {
                label: 'Set parent ID',
                testId: 'set-parent-id',
                onClick: () => {
                  setParentIdModal(customer);
                  setParentIdValue('');
                },
              },
          // Shared means SocialBAY already has the office SSO linkage, so enabling again is a
          // no-op; use Disconnect Social for the teardown that turns it back off.
          {
            label: 'Enable Social',
            testId: 'enable-social',
            disabled: customer.platform === 'Shared',
            disabledReason: 'Social is already enabled for this customer.',
            href: routes.customerEnableSocial(String(customer.companyid)),
          },
          // Full teardown lives in the Disconnect Social wizard, which resolves the
          // customer's agents/feeds and requires agreeing before it runs.
          {
            label: 'Disconnect Social',
            testId: 'disconnect-social',
            destructive: true,
            disabled: !customer.bay_parent_account_id,
            disabledReason: 'Social is not connected for this customer.',
            href: routes.customerDisconnectSocial(String(customer.companyid)),
          },
        ];
        return (
          <RowActionsMenu testId={`customers-table-actions-${customer.companyid}`} items={items} />
        );
      },
    },
  ];

  const agentColumns: PanelTableColumn<CustomerAgent>[] = [
    { header: 'First', render: (agent) => agent.firstname },
    { header: 'Last', render: (agent) => agent.lastname },
    { header: 'Email', render: (agent) => agent.email },
    { header: 'BAY account', render: (agent) => agent.bay_account_id },
    ...(
      [
        ['listings', 'Listings'],
        ['profiles', 'Profile'],
        ['logins', 'Allow login'],
      ] as const
    ).map(([kind, header]) => ({
      header,
      render: (agent: CustomerAgent) => (
        <Switch
          checked={agentSelections[agent.publicid]?.[kind] ?? false}
          onChange={() => toggleAgent(agent.publicid, kind)}
          ariaLabel={`${header} for ${agent.firstname} ${agent.lastname}`}
          testId={`customers-agent-${kind}-${agent.publicid}`}
        />
      ),
    })),
  ];

  return (
    <PageShell>
      <PageHeader
        title="Customers"
        breadcrumbs={[{ label: 'Customers' }]}
        actions={
          canOnboardCustomers(getAccess())
            ? [
                {
                  label: 'Onboard',
                  kind: 'primary',
                  testId: 'customers-onboard-button',
                  href: routes.customerNew,
                },
              ]
            : []
        }
      />

      <StatsRow ariaLabel="Customer statistics">
        {(Object.keys(STAT_TITLES) as (keyof typeof STAT_TITLES)[]).map((key) =>
          customersQuery.isLoading ? (
            <StatTileSkeleton key={key} />
          ) : (
            <StatTile
              key={key}
              label={STAT_TITLES[key]}
              value={customers ? stats[key] : undefined}
            />
          ),
        )}
      </StatsRow>

      <Card>
        <ListToolbar
          title="All customers"
          description={CUSTOMER_OPS_SUBTITLE}
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search customers…',
            testId: 'customers-search',
          }}
          filters={{
            count: list.filterCount,
            onClick: () => setFilterOpen(true),
            onClear: list.clear,
            testId: 'customers-filter-button',
          }}
        />
        <CardBody className="p-0">
          <DataTable<Customer>
            framed={false}
            testId="customers-table"
            columns={customerColumns}
            data={pageRows}
            rowKey={(customer) => String(customer.companyid)}
            loading={customersQuery.isLoading}
            refreshing={customersQuery.isFetching && !customersQuery.isLoading}
            error={
              customersQuery.error
                ? {
                    what: 'the customers',
                    message: queryErrorMessage(customersQuery.error),
                    onRetry: () => customersQuery.refetch(),
                  }
                : undefined
            }
            emptyMessage={filtered ? 'No customers match.' : 'No customers yet.'}
            emptyDescription={
              filtered
                ? 'Clear the search or filters to see more customers.'
                : 'Customers appear here once they are onboarded.'
            }
          />
        </CardBody>
        <Pagination
          pageIndex={page - 1}
          pageSize={list.pageSize}
          pageCount={pages}
          totalRows={filteredCustomers.length}
          onPageChange={(index) => list.setPage(index + 1)}
          pageSizeControl={{
            value: list.pageSize,
            onChange: list.setPageSize,
            options: [...list.pageSizes],
          }}
        />
      </Card>

      <FilterDrawer<Record<string, string>>
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        value={list.filters}
        onApply={list.setFilters}
        onClear={list.clear}
        testId="customers-filter-drawer"
      >
        {(draft, patch) => (
          <>
            <Select
              id="customers-filter-platform"
              testId="customers-filter-platform-select"
              label="Platform"
              placeholder="All platforms"
              placeholderSelectable
              value={draft.platform ?? ''}
              options={PLATFORMS.map((p) => ({ value: p, label: p }))}
              onChange={(platform) => patch({ platform })}
            />
            <Select
              id="customers-filter-feed"
              testId="customers-filter-feed-select"
              label="Feed"
              placeholder="All feeds"
              placeholderSelectable
              value={draft.feed ?? ''}
              options={filterOptions.feed.map((f) => ({ value: f, label: f }))}
              onChange={(feed) => patch({ feed })}
            />
            <Select
              id="customers-filter-dataserver"
              testId="customers-filter-dataserver-select"
              label="Data server"
              placeholder="All data servers"
              placeholderSelectable
              value={draft.dataserver ?? ''}
              options={filterOptions.dataserver.map((d) => ({ value: d, label: d }))}
              onChange={(dataserver) => patch({ dataserver })}
            />
          </>
        )}
      </FilterDrawer>

      <Modal
        isOpen={!!agentsModal}
        onClose={() => setAgentsModal(null)}
        title={`Social sync for ${agentsModal?.companyid ?? ''}`}
        size="xlarge"
        testId="customers-agents-modal"
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {changeCount ? `${changeCount} change${changeCount === 1 ? '' : 's'}` : 'No changes'}
            </span>
            <FormActions
              discardLabel="Close"
              onDiscard={() => setAgentsModal(null)}
              onSave={saveAgents}
              busy={isSaving}
              disabled={!changeCount}
              disabledReason="No changes to save"
              saveTestId="customers-agents-save-button"
            />
          </div>
        }
      >
        {!agents ? (
          <SkeletonRows />
        ) : (
          <PanelTable<CustomerAgent>
            ariaLabel="Agents"
            testId="customers-agents-table"
            columns={agentColumns}
            data={agents}
            getRowId={(agent) => agent.publicid}
            emptyMessage="No agents under this customer."
            emptyDescription="Agents appear here once they are added to the customer's account."
          />
        )}
      </Modal>

      <Modal
        isOpen={!!parentIdModal}
        onClose={() => setParentIdModal(null)}
        title={`Set parent account ID for ${parentIdModal?.companyid ?? ''}`}
        size="small"
        testId="customers-parent-id-modal"
        footer={
          <FormActions
            discardLabel="Close"
            onDiscard={() => setParentIdModal(null)}
            onSave={saveParentId}
            busy={isSaving}
            disabled={!parentIdValue.trim()}
            disabledReason="Enter the parent account ID"
            saveTestId="customers-parent-id-save-button"
          />
        }
      >
        <TextField
          id="customers-parent-id"
          testId="customers-parent-id-input"
          label="BAY parent account ID"
          value={parentIdValue}
          onChange={setParentIdValue}
        />
      </Modal>

      <Modal
        isOpen={!!domainsModal}
        onClose={() => setDomainsModal(null)}
        title={`Sites for ${customerLabel(domainsModal)}`}
        testId="customers-domains-modal"
        footer={<ModalActions onDismiss={() => setDomainsModal(null)} />}
      >
        <ul className="max-h-[60vh] divide-y divide-gray-100 overflow-auto text-sm text-gray-800 dark:divide-white/[0.04] dark:text-white/90">
          {domainsOf(domainsModal).map((d, i) => (
            <li key={i} className="break-all py-2">
              <DomainLink value={d} />
            </li>
          ))}
        </ul>
      </Modal>

      <Modal
        isOpen={!!feedsModal}
        onClose={() => setFeedsModal(null)}
        title={`Feeds for ${customerLabel(feedsModal)}`}
        testId="customers-feeds-modal"
        footer={<ModalActions onDismiss={() => setFeedsModal(null)} />}
      >
        <ul className="max-h-[60vh] divide-y divide-gray-100 overflow-auto text-sm text-gray-800 dark:divide-white/[0.04] dark:text-white/90">
          {feedsOf(feedsModal).map((f, i) => (
            <li key={i} className="break-all py-2">
              {f}
            </li>
          ))}
        </ul>
      </Modal>
    </PageShell>
  );
};
